import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import Hls from 'hls.js';
import { useSearchParams } from 'react-router-dom';
import type {
  AgentWorkspace,
  MysteryShopDetail,
  MysteryShopEvent,
  MysteryShopFinding,
  MysteryShopJob,
  MysteryShopJobStatus,
  MysteryShopLiveView,
  MysteryShopReplay,
} from '@servicesai/api-client';
import { GlassButton, GlassCard, StatusPill, useI18n } from '@servicesai/ui';
import { admin } from '../../lib/api.js';

const ACTIVE_STATUSES = new Set<MysteryShopJobStatus>(['queued', 'running', 'paused']);
const EVENT_STYLE: { [kind: string]: { icon: string; classes: string } } = {
  phase: { icon: '◆', classes: 'border-seablue-200 bg-seablue-50 text-oasis' },
  action: { icon: '↗', classes: 'border-aegold-300 bg-aegold-50 text-gold-deep' },
  observation: { icon: '◎', classes: 'border-aeblack-100 bg-white text-ink-soft' },
  finding: { icon: '!', classes: 'border-aered-200 bg-aered-50 text-reject' },
  checklist: { icon: '✓', classes: 'border-aegreen-200 bg-aegreen-50 text-aegreen-700' },
  blocked: { icon: '×', classes: 'border-aered-300 bg-aered-50 text-reject' },
  control: { icon: '◉', classes: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700' },
  error: { icon: '⚠', classes: 'border-aered-300 bg-aered-50 text-reject' },
};

function titleCase(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function pagePath(url: string): string {
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url || '—';
  }
}

function runTone(status: MysteryShopJobStatus) {
  if (status === 'succeeded') return 'published' as const;
  if (status === 'failed' || status === 'cancelled' || status === 'timed_out') return 'rejected' as const;
  if (status === 'paused') return 'in_review' as const;
  return 'in_progress' as const;
}

function elapsedLabel(job: MysteryShopJob, now: number): string {
  const start = Date.parse(job.startedAt ?? job.createdAt);
  const end = job.finishedAt ? Date.parse(job.finishedAt) : now;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '—';
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function ProgressRing({ score }: { score: number | null }) {
  const value = Math.max(0, Math.min(100, score ?? 0));
  return (
    <div
      role="img"
      aria-label={`Score ${score ?? 'unscored'}`}
      className="relative grid h-32 w-32 shrink-0 place-items-center rounded-full"
      style={{ background: `conic-gradient(${value >= 80 ? '#10b981' : value >= 60 ? '#b68a35' : '#c83c36'} ${value * 3.6}deg, #e8ebee 0deg)` }}
    >
      <div className="grid h-24 w-24 place-items-center rounded-full bg-white text-center shadow-inner">
        <div><div className="font-mono text-3xl font-black">{score == null ? '—' : Math.round(score)}</div><div className="text-[9px] font-bold uppercase tracking-wider text-ink-faint">experience score</div></div>
      </div>
    </div>
  );
}

function ReplayPlayer({
  jobId,
  pageId,
  seekTarget,
  videoRef,
}: {
  jobId: string;
  pageId: string | null;
  seekTarget: { ms: number; token: number } | null;
  videoRef: RefObject<HTMLVideoElement | null>;
}) {
  const { lang } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageId) return;
    let disposed = false;
    let playlistUrl: string | null = null;
    let hls: Hls | null = null;
    setError(null);
    admin().mysteryShopReplayPlaylist(jobId, pageId)
      .then((playlist) => {
        if (disposed) return;
        playlistUrl = URL.createObjectURL(new Blob([playlist], { type: 'application/vnd.apple.mpegurl' }));
        if (Hls.isSupported()) {
          hls = new Hls({ enableWorker: true, lowLatencyMode: false });
          hls.loadSource(playlistUrl);
          hls.attachMedia(video);
          hls.on(Hls.Events.ERROR, (_event, detail) => {
            if (detail.fatal && !disposed) setError(lang === 'ar' ? 'تعذر تحميل دليل الإعادة.' : 'Replay evidence could not be loaded.');
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = playlistUrl;
        } else {
          setError(lang === 'ar' ? 'لا يدعم هذا المتصفح إعادة HLS.' : 'This browser cannot play HLS replay.');
        }
      })
      .catch((reason: Error) => {
        if (!disposed) setError(reason.message);
      });
    return () => {
      disposed = true;
      hls?.destroy();
      if (playlistUrl) URL.revokeObjectURL(playlistUrl);
      video.removeAttribute('src');
      video.load();
    };
  }, [jobId, lang, pageId, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && seekTarget) {
      const seek = () => { video.currentTime = seekTarget.ms / 1000; };
      if (video.readyState >= 1) seek();
      else video.addEventListener('loadedmetadata', seek, { once: true });
    }
  }, [seekTarget, videoRef]);

  return (
    <div className="relative h-full w-full bg-black">
      <video ref={videoRef} controls className="h-full w-full object-contain" aria-label={lang === 'ar' ? 'إعادة جلسة المتصفح' : 'Browser session replay'} />
      <label className="absolute end-3 top-3 rounded-lg border border-white/20 bg-black/70 px-2 py-1 text-[10px] font-bold text-white backdrop-blur">
        {lang === 'ar' ? 'السرعة' : 'Speed'}
        <select
          aria-label={lang === 'ar' ? 'سرعة الإعادة' : 'Replay speed'}
          value={speed}
          onChange={(event) => {
            const value = Number(event.target.value);
            setSpeed(value);
            if (videoRef.current) videoRef.current.playbackRate = value;
          }}
          className="ms-2 bg-transparent font-mono"
        >
          {[0.5, 1, 1.5, 2].map((value) => <option key={value} value={value} className="text-black">{value}×</option>)}
        </select>
      </label>
      {error && <div role="alert" className="absolute inset-x-4 bottom-16 rounded-lg bg-aered-600/90 p-3 text-xs font-bold text-white">{error}</div>}
    </div>
  );
}

function RouteCoverageMap({
  detail,
  selectedPageId,
  onSelectPage,
}: {
  detail: MysteryShopDetail;
  selectedPageId: string | null;
  onSelectPage: (pageId: string) => void;
}) {
  const { lang } = useI18n();
  const nodes: Node[] = detail.pages.map((page, index) => {
    const findings = detail.findings.filter((finding) => finding.pageId === page.id);
    const failures = findings.filter((finding) => !finding.isPositive).length;
    return {
      id: page.id,
      position: { x: index * 225, y: page.depth * 120 },
      data: {
        label: (
          <div className="w-40 text-start">
            <div className="truncate text-[10px] font-extrabold">{page.title || pagePath(page.url)}</div>
            <div className="mt-1 flex items-center justify-between text-[9px] text-ink-faint"><span>depth {page.depth}</span><span className={failures ? 'text-reject' : 'text-aegreen-700'}>{failures ? `${failures} issues` : '✓ clear'}</span></div>
          </div>
        ),
      },
      style: {
        borderRadius: 12,
        border: selectedPageId === page.id ? '2px solid #0173ab' : failures ? '1px solid #e4a09c' : '1px solid #cfd5da',
        background: selectedPageId === page.id ? '#eaf7fd' : '#fff',
        boxShadow: selectedPageId === page.id ? '0 0 0 3px rgba(1,115,171,.12)' : '0 4px 12px rgba(0,0,0,.05)',
        padding: 10,
      },
    };
  });
  const edges: Edge[] = detail.pages.slice(1).map((page, index) => {
    const parent = [...detail.pages.slice(0, index + 1)].reverse().find((candidate) => candidate.depth < page.depth)
      ?? detail.pages[index]!;
    return {
      id: `${parent.id}-${page.id}`,
      source: parent.id,
      target: page.id,
      animated: page.status === 'discovered',
      style: { stroke: '#7b8790' },
    };
  });
  return (
    <div className="h-80 overflow-hidden rounded-xl border border-aeblack-100 bg-white" dir="ltr">
      <ReactFlow
        key={detail.pages.map((page) => page.id).join(',')}
        nodes={nodes}
        edges={edges}
        fitView
        minZoom={0.35}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={(_event, node) => onSelectPage(node.id)}
        aria-label={lang === 'ar' ? 'خريطة تغطية المسارات' : 'Visual route coverage map'}
      >
        <Background color="#e5e8eb" gap={18} />
        <MiniMap pannable zoomable nodeColor={(node) => node.id === selectedPageId ? '#0173ab' : '#cfd5da'} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

function FindingCard({
  finding,
  selected,
  onSelect,
  onScreenshot,
  onReplay,
}: {
  finding: MysteryShopFinding;
  selected: boolean;
  onSelect: () => void;
  onScreenshot: (() => void) | null;
  onReplay: (() => void) | null;
}) {
  const { lang } = useI18n();
  const isFix = finding.title.toLowerCase().startsWith('fix next');
  const tone = finding.isPositive
    ? 'border-aegreen-200 bg-aegreen-50'
    : isFix
      ? 'border-aegold-300 bg-aegold-50'
      : 'border-aered-200 bg-aered-50';
  return (
    <article
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      className={[
        'cursor-pointer rounded-xl border p-3 transition',
        tone,
        selected ? 'ring-2 ring-seablue-300' : 'hover:shadow-sm',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] font-bold uppercase text-ink-faint">{titleCase(finding.dimension)}</div>
        <span className="rounded-full bg-white/70 px-2 py-0.5 text-[9px] font-extrabold">{titleCase(finding.severity)}</span>
      </div>
      <h5 className="mt-1 text-xs font-extrabold">{finding.title}</h5>
      <p className="mt-1 line-clamp-3 text-[10px] leading-4 text-ink-soft">{finding.detail}</p>
      {(onScreenshot || onReplay) && (
        <div className="mt-2 flex gap-2">
          {onScreenshot && <button type="button" onClick={(event) => { event.stopPropagation(); onScreenshot(); }} className="rounded-md bg-white/80 px-2 py-1 text-[9px] font-bold">▧ {lang === 'ar' ? 'لقطة' : 'Screenshot'}</button>}
          {onReplay && <button type="button" onClick={(event) => { event.stopPropagation(); onReplay(); }} className="rounded-md bg-white/80 px-2 py-1 text-[9px] font-bold">▶ {lang === 'ar' ? 'إعادة' : 'Replay'}</button>}
        </div>
      )}
    </article>
  );
}

export function MysteryShopperOperationsPanel({ workspace }: { workspace: AgentWorkspace }) {
  const { lang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRunId = searchParams.get('run');
  const text = useCallback((en: string, ar: string) => lang === 'ar' ? ar : en, [lang]);
  const config = workspace.effectiveConfig.specialization?.mysteryShopper;
  const targets = config?.targets ?? [];
  const [targetId, setTargetId] = useState(config?.defaultTargetId ?? targets[0]?.id ?? '');
  const target = targets.find((candidate) => candidate.id === targetId) ?? targets[0] ?? null;
  const [scenarioIds, setScenarioIds] = useState<string[]>([]);
  const [runs, setRuns] = useState<MysteryShopJob[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MysteryShopDetail | null>(null);
  const [liveView, setLiveView] = useState<MysteryShopLiveView | null>(null);
  const [replay, setReplay] = useState<MysteryShopReplay | null>(null);
  const [replayPageId, setReplayPageId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'evidence' | 'replay'>('evidence');
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [artifactUrls, setArtifactUrls] = useState<Map<string, string>>(new Map());
  const [artifactFailures, setArtifactFailures] = useState<Set<string>>(new Set());
  const [artifactLoading, setArtifactLoading] = useState<Set<string>>(new Set());
  const [artifactRetry, setArtifactRetry] = useState(0);
  const [interactive, setInteractive] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [emailingRunId, setEmailingRunId] = useState<string | null>(null);
  const [emailNotice, setEmailNotice] = useState<{
    runId: string;
    message: string;
  } | null>(null);
  const [controlling, setControlling] = useState(false);
  const [controlAction, setControlAction] = useState<string | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [seekTarget, setSeekTarget] = useState<{ ms: number; token: number } | null>(null);
  const [canTakeControl, setCanTakeControl] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const runsRequestRef = useRef(0);
  const detailRequestRef = useRef(0);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [fullscreen]);

  useEffect(() => {
    let cancelled = false;
    admin().me()
      .then((staff) => {
        if (!cancelled) {
          setCanTakeControl(
            staff.roles.includes('approver') || staff.roles.includes('admin'),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCanTakeControl(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setScenarioIds([]);
  }, [targetId]);

  const loadRuns = useCallback(async (preferredId?: string | null) => {
    const requestId = ++runsRequestRef.current;
    setLoadingRuns(true);
    try {
      const result = await admin().mysteryShopRuns({ limit: 50 });
      if (requestId !== runsRequestRef.current) return;
      setRuns(result.jobs);
      setSelectedId((existing) => {
        const preferred = preferredId ?? existing;
        return preferred && result.jobs.some((job) => job.id === preferred)
          ? preferred
          : result.jobs[0]?.id ?? null;
      });
    } catch (reason) {
      if (requestId === runsRequestRef.current) setError((reason as Error).message);
    } finally {
      if (requestId === runsRequestRef.current) setLoadingRuns(false);
    }
  }, []);

  const loadDetail = useCallback(async (jobId: string, quiet = false) => {
    const requestId = ++detailRequestRef.current;
    if (!quiet) setLoadingDetail(true);
    try {
      const result = await admin().mysteryShopRun(jobId);
      if (requestId !== detailRequestRef.current || result.job.id !== jobId) return;
      setDetail(result);
      setRuns((current) => current.map((job) => job.id === result.job.id ? result.job : job));
      setSelectedPageId((current) =>
        current && result.pages.some((page) => page.id === current)
          ? current
          : result.pages[0]?.id ?? null);
      setSelectedArtifactId((current) =>
        current && result.artifacts.some((artifact) => artifact.id === current)
          ? current
          : result.pages.find((page) => page.screenshotArtifactId)?.screenshotArtifactId
            ?? result.artifacts.find((artifact) => artifact.kind === 'screenshot')?.id
            ?? null);
    } catch (reason) {
      if (requestId === detailRequestRef.current) setError((reason as Error).message);
    } finally {
      if (!quiet && requestId === detailRequestRef.current) setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void loadRuns(requestedRunId);
  }, [loadRuns, requestedRunId]);

  useEffect(() => {
    detailRequestRef.current += 1;
    setDetail(null);
    setLiveView(null);
    setReplay(null);
    setReplayPageId(null);
    setSelectedFindingId(null);
    setSelectedEventId(null);
    setInteractive(false);
    if (selectedId) void loadDetail(selectedId);
  }, [loadDetail, selectedId]);

  const selectedJob = detail?.job ?? runs.find((job) => job.id === selectedId) ?? null;
  const active = selectedJob ? ACTIVE_STATUSES.has(selectedJob.status) : false;

  const selectRun = useCallback((jobId: string) => {
    setSelectedId(jobId);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'operations');
    next.set('run', jobId);
    setSearchParams(next, { replace: true });
    if (window.matchMedia('(max-width: 1279px)').matches) {
      window.requestAnimationFrame(() => {
        document.getElementById('mystery-browser-theater')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (
      requestedRunId
      && requestedRunId !== selectedId
      && runs.some((job) => job.id === requestedRunId)
    ) {
      setSelectedId(requestedRunId);
    }
  }, [requestedRunId, runs, selectedId]);

  useEffect(() => {
    if (!selectedId || !active) return;
    const interval = window.setInterval(() => void loadDetail(selectedId, true), 3_000);
    return () => window.clearInterval(interval);
  }, [active, loadDetail, selectedId]);

  useEffect(() => {
    if (!selectedId || !active) return;
    const controller = new AbortController();
    let lastEventId: string | undefined;
    const connect = async () => {
      while (!controller.signal.aborted) {
        try {
          await admin().mysteryShopRunEvents(
            selectedId,
            controller.signal,
            (streamEvent) => {
              if (streamEvent.id) lastEventId = streamEvent.id;
              if ('kind' in streamEvent.data) {
                const incoming = streamEvent.data;
                setDetail((current) => current && current.job.id === selectedId
                  ? {
                      ...current,
                      events: current.events.some((event) => event.id === incoming.id)
                        ? current.events
                        : [...current.events, incoming],
                    }
                  : current);
              } else if (streamEvent.event === 'status' || streamEvent.event === 'done') {
                void loadDetail(selectedId, true);
              }
            },
            lastEventId,
          );
        } catch (reason) {
          if (controller.signal.aborted) return;
          setError((reason as Error).message);
        }
        await new Promise<void>((resolve) => {
          const onAbort = () => {
            window.clearTimeout(timeout);
            resolve();
          };
          const timeout = window.setTimeout(() => {
            controller.signal.removeEventListener('abort', onAbort);
            resolve();
          }, 1_000);
          controller.signal.addEventListener('abort', onAbort, { once: true });
        });
      }
    };
    void connect();
    return () => controller.abort();
  }, [active, loadDetail, selectedId]);

  useEffect(() => {
    if (
      !selectedJob
      || selectedJob.status !== 'succeeded'
      || selectedJob.output?.provider !== 'browserbase'
    ) {
      setReplay(null);
      setReplayPageId(null);
      setViewMode('evidence');
      return;
    }
    let cancelled = false;
    let retryTimer: number | undefined;
    let attempt = 0;
    const loadReplay = async () => {
      try {
        const result = await admin().mysteryShopReplay(selectedJob.id);
        if (cancelled) return;
        setReplay(result);
        setReplayPageId((current) =>
          current && result.pages.some((page) => page.pageId === current)
            ? current
            : result.pages[0]?.pageId ?? null);
        setViewMode((current) =>
          current === 'replay' && result.pages.length === 0 ? 'evidence' : current);
      } catch {
        if (cancelled || attempt >= 11) {
          if (!cancelled) setViewMode('evidence');
          return;
        }
        attempt += 1;
        retryTimer = window.setTimeout(
          () => void loadReplay(),
          Math.min(15_000, 1_000 * 2 ** Math.min(attempt, 4)),
        );
      }
    };
    void loadReplay();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [selectedJob?.id, selectedJob?.output?.provider, selectedJob?.status]);

  const artifactJobId = detail?.job.id ?? null;
  const screenshotIds = (detail?.artifacts ?? [])
    .filter((artifact) => artifact.kind === 'screenshot')
    .map((artifact) => artifact.id);
  const screenshotArtifactKey = [
    ...new Set([
      ...screenshotIds.slice(-16),
      ...(selectedArtifactId ? [selectedArtifactId] : []),
    ]),
  ].join(',');

  useEffect(() => {
    if (!artifactJobId) {
      setArtifactUrls(new Map());
      setArtifactFailures(new Set());
      setArtifactLoading(new Set());
      return;
    }
    let disposed = false;
    const created: string[] = [];
    const artifactIds = screenshotArtifactKey ? screenshotArtifactKey.split(',') : [];
    setArtifactLoading(new Set(artifactIds));
    setArtifactFailures(new Set());
    Promise.allSettled(artifactIds.map(async (artifactId) => {
      const blob = await admin().mysteryShopArtifact(artifactJobId, artifactId);
      const url = URL.createObjectURL(blob);
      created.push(url);
      return [artifactId, url] as const;
    }))
      .then((results) => {
        if (disposed) {
          created.forEach((url) => URL.revokeObjectURL(url));
          return;
        }
        const entries = results.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value] : []);
        const failures = artifactIds.filter(
          (_artifactId, index) => results[index]?.status === 'rejected',
        );
        setArtifactUrls(new Map(entries));
        setArtifactFailures(new Set(failures));
        setArtifactLoading(new Set());
      });
    return () => {
      disposed = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [artifactJobId, artifactRetry, screenshotArtifactKey]);

  const launch = async () => {
    if (!target) return;
    setLaunching(true);
    setError(null);
    try {
      const result = await admin().enqueueMysteryShopRuns({
        targetId: target.id,
        ...(scenarioIds.length ? { scenarioIds } : {}),
      });
      const jobId = result.jobs[0]?.job.id ?? null;
      await loadRuns(jobId);
      if (jobId) selectRun(jobId);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setLaunching(false);
    }
  };

  const emailReport = async () => {
    if (!selectedJob || emailingRunId) return;
    const runId = selectedJob.id;
    setEmailingRunId(runId);
    setEmailNotice(null);
    setError(null);
    try {
      const result = await admin().emailMysteryShopReport(
        selectedJob.id,
        lang,
      );
      const acceptedAt = new Date(result.sent.acceptedAt).toLocaleTimeString(
        lang === 'ar' ? 'ar-AE' : 'en-GB',
        { hour: '2-digit', minute: '2-digit' },
      );
      setEmailNotice({
        runId,
        message: text(
          `Report queued at ${acceptedAt} in ${lang === 'ar' ? 'Arabic' : 'English'}.`,
          `تمت جدولة التقرير الساعة ${acceptedAt} باللغة ${lang === 'ar' ? 'العربية' : 'الإنجليزية'}.`,
        ),
      });
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setEmailingRunId(null);
    }
  };

  const control = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!selectedJob || controlAction) return;
    setControlAction(action);
    setError(null);
    try {
      const result = action === 'pause'
        ? await admin().pauseMysteryShopRun(selectedJob.id, selectedJob.updatedAt)
        : action === 'resume'
          ? await admin().resumeMysteryShopRun(selectedJob.id, selectedJob.updatedAt)
          : await admin().cancelMysteryShopRun(selectedJob.id, selectedJob.updatedAt);
      setDetail((current) => current ? { ...current, job: result.job } : current);
      await loadRuns(result.job.id);
    } catch (reason) {
      setError((reason as Error).message);
      await loadDetail(selectedJob.id, true);
    } finally {
      setControlAction(null);
    }
  };

  const takeControl = async () => {
    if (!selectedJob || controlling || !canTakeControl) return;
    setControlling(true);
    setError(null);
    try {
      let paused = await admin().pauseMysteryShopRun(
        selectedJob.id,
        selectedJob.updatedAt,
      );
      const deadline = Date.now() + 30_000;
      while (paused.job.status !== 'paused' && Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        const refreshed = await admin().mysteryShopRun(selectedJob.id);
        setDetail(refreshed);
        paused = { job: refreshed.job };
      }
      if (paused.job.status !== 'paused') {
        throw new Error(text(
          'The browser worker did not acknowledge a safe pause in time.',
          'لم يؤكد عامل المتصفح الإيقاف الآمن في الوقت المحدد.',
        ));
      }
      setDetail((current) => current ? { ...current, job: paused.job } : current);
      const result = await admin().mysteryShopLiveView(selectedJob.id);
      setLiveView(result);
      setInteractive(true);
    } catch (reason) {
      setError((reason as Error).message);
      try {
        const current = await admin().mysteryShopRun(selectedJob.id);
        if (current.job.status === 'paused') {
          await admin().resumeMysteryShopRun(
            selectedJob.id,
            current.job.updatedAt,
          );
        }
      } catch {
        // The durable paused state remains visible for explicit operator recovery.
      }
      await loadDetail(selectedJob.id, true);
    } finally {
      setControlling(false);
    }
  };

  const returnControl = async () => {
    if (!selectedJob || controlling) return;
    setControlling(true);
    setError(null);
    try {
      const resumed = await admin().resumeMysteryShopRun(selectedJob.id, selectedJob.updatedAt);
      setDetail((current) => current ? { ...current, job: resumed.job } : current);
      setLiveView(null);
      setInteractive(false);
    } catch (reason) {
      setError((reason as Error).message);
      await loadDetail(selectedJob.id, true);
    } finally {
      setControlling(false);
    }
  };

  const selectedPage = detail?.pages.find((page) => page.id === selectedPageId) ?? detail?.pages[0] ?? null;
  const selectedLivePage = liveView?.pages.find((page) => page.url === selectedPage?.url) ?? liveView?.pages[0] ?? null;
  const heroUrl = selectedArtifactId ? artifactUrls.get(selectedArtifactId) : undefined;
  const heroLoading = Boolean(
    selectedArtifactId && artifactLoading.has(selectedArtifactId),
  );
  const heroFailed = Boolean(
    selectedArtifactId && artifactFailures.has(selectedArtifactId),
  );
  const latestEvent = detail?.events.at(-1) ?? null;
  const currentChecklist = [...(detail?.events ?? [])].reverse().find((event) => event.kind === 'checklist');
  const issueCounts = {
    critical: detail?.findings.filter((finding) => finding.severity === 'critical').length ?? 0,
    high: detail?.findings.filter((finding) => finding.severity === 'high').length ?? 0,
    warning: detail?.findings.filter((finding) => finding.result === 'warning').length ?? 0,
  };
  const coverage = selectedJob && selectedJob.pagesDiscovered > 0
    ? Math.round((selectedJob.pagesVisited / selectedJob.pagesDiscovered) * 100)
    : 0;

  const selectEvidencePage = (pageId: string) => {
    const page = detail?.pages.find((candidate) => candidate.id === pageId);
    if (!page) return;
    setSelectedPageId(page.id);
    if (page.screenshotArtifactId) setSelectedArtifactId(page.screenshotArtifactId);
    if (
      page.pageId
      && replay?.pages.some((candidate) => candidate.pageId === page.pageId)
    ) {
      setReplayPageId(page.pageId);
    }
  };

  const selectFinding = (finding: MysteryShopFinding) => {
    setSelectedFindingId(finding.id);
    if (finding.pageId) selectEvidencePage(finding.pageId);
  };
  const showFindingScreenshot = (finding: MysteryShopFinding) => {
    selectFinding(finding);
    if (finding.screenshotArtifactId) setSelectedArtifactId(finding.screenshotArtifactId);
    setViewMode('evidence');
  };
  const showFindingReplay = (finding: MysteryShopFinding) => {
    selectFinding(finding);
    const providerPageId = finding.pageId
      ? detail?.pages.find((page) => page.id === finding.pageId)?.pageId
      : null;
    const timestamp = finding.replayTimestampMs;
    const replayPage = replay?.pages.find((page) => page.pageId === providerPageId)
      ?? (
        timestamp === null
          ? undefined
          : replay?.pages.find(
              (page) =>
                timestamp >= page.startTimeMs
                && timestamp <= page.endTimeMs,
            )
      )
      ?? replay?.pages[0];
    if (replayPage) setReplayPageId(replayPage.pageId);
    if (finding.replayTimestampMs !== null) {
      setSeekTarget({
        ms: Math.max(
          0,
          finding.replayTimestampMs - (replayPage?.startTimeMs ?? 0),
        ),
        token: Date.now(),
      });
    }
    setViewMode('replay');
  };
  const selectEvent = (event: MysteryShopEvent) => {
    setSelectedEventId(event.id);
    if (event.pageUrl && detail) {
      const page = detail.pages.find((candidate) =>
        candidate.url === event.pageUrl || candidate.normalizedUrl === event.pageUrl);
      if (page) {
        selectEvidencePage(page.id);
      }
    }
    const findingId = typeof event.data.findingId === 'string' ? event.data.findingId : null;
    const finding = findingId ? detail?.findings.find((candidate) => candidate.id === findingId) : null;
    if (finding) selectFinding(finding);
  };

  const theater = (
    <div id="mystery-browser-theater" className={[
      'overflow-hidden border border-[#172f3a] bg-[#07171f] text-white shadow-2xl',
      fullscreen ? 'fixed inset-3 z-[100] flex flex-col rounded-2xl' : 'rounded-2xl',
    ].join(' ')}
      role={fullscreen ? 'dialog' : undefined}
      aria-modal={fullscreen || undefined}
      aria-label={fullscreen ? text('Browser evidence fullscreen', 'دليل المتصفح بملء الشاشة') : undefined}
    >
      <div className="flex min-h-12 items-center gap-3 border-b border-white/10 bg-[#0c2029] px-3">
        <div className="hidden gap-1.5 sm:flex"><span className="h-2.5 w-2.5 rounded-full bg-[#ff6b61]" /><span className="h-2.5 w-2.5 rounded-full bg-[#f4bd4f]" /><span className="h-2.5 w-2.5 rounded-full bg-[#54c45e]" /></div>
        <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
          {(liveView?.pages.length ? liveView.pages : detail?.pages ?? []).slice(0, 8).map((page, index) => {
            const pageKey = 'id' in page ? page.id : String(index);
            const pageUrl = page.url;
            const selected = selectedLivePage?.url === pageUrl || selectedPage?.url === pageUrl;
            return (
              <button
                type="button"
                key={pageKey}
                onClick={() => {
                  const matching = detail?.pages.find((candidate) => candidate.url === pageUrl);
                  if (matching) selectEvidencePage(matching.id);
                }}
                className={`max-w-44 truncate rounded-t-lg px-3 py-2 text-[10px] font-bold ${selected ? 'bg-[#173746] text-white' : 'text-white/50 hover:bg-white/5'}`}
              >
                {('title' in page && page.title) || pagePath(pageUrl) || `Page ${index + 1}`}
              </button>
            );
          })}
        </div>
        {replay && (
          <div role="group" aria-label={text('Browser evidence mode', 'وضع دليل المتصفح')} className="flex rounded-lg bg-black/30 p-0.5">
            <button type="button" aria-pressed={viewMode === 'evidence'} onClick={() => setViewMode('evidence')} className={`rounded-md px-2 py-1 text-[9px] font-bold ${viewMode === 'evidence' ? 'bg-white text-[#07171f]' : 'text-white/60'}`}>{text('Evidence', 'الدليل')}</button>
            <button type="button" aria-pressed={viewMode === 'replay'} onClick={() => setViewMode('replay')} className={`rounded-md px-2 py-1 text-[9px] font-bold ${viewMode === 'replay' ? 'bg-white text-[#07171f]' : 'text-white/60'}`}>{text('Replay', 'الإعادة')}</button>
          </div>
        )}
        <button type="button" onClick={() => setFullscreen((value) => !value)} className="grid h-9 w-9 place-items-center rounded-lg text-white/70 hover:bg-white/10" aria-label={fullscreen ? text('Exit fullscreen', 'إنهاء ملء الشاشة') : text('Fullscreen', 'ملء الشاشة')}>{fullscreen ? '↙' : '↗'}</button>
      </div>
      <div className="flex min-h-10 items-center gap-2 border-b border-white/10 bg-[#102832] px-3">
        <span className="text-aegreen-400">●</span>
        <div className="min-w-0 flex-1 truncate rounded-md bg-black/25 px-3 py-1.5 font-mono text-[10px] text-white/70" dir="ltr">
          {selectedLivePage?.url ?? selectedPage?.url ?? selectedJob?.target.startUrl ?? 'about:blank'}
        </div>
        <StatusPill state={selectedJob ? runTone(selectedJob.status) : 'draft'} label={selectedJob ? titleCase(selectedJob.status) : text('No run', 'لا يوجد تشغيل')} />
      </div>
      <div className={`relative aspect-video min-h-0 bg-[#061016] ${fullscreen ? 'flex-1' : ''}`}>
        {viewMode === 'replay' && replay && replayPageId ? (
          <ReplayPlayer jobId={selectedJob!.id} pageId={replayPageId} seekTarget={seekTarget} videoRef={videoRef} />
        ) : liveView && selectedLivePage && active ? (
          <iframe
            src={selectedLivePage.debuggerFullscreenUrl}
            title={text('Live browser session', 'جلسة المتصفح المباشرة')}
            className="h-full w-full border-0 bg-white"
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-forms allow-same-origin"
            style={{ pointerEvents: interactive ? 'auto' : 'none' }}
          />
        ) : heroLoading ? (
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              <p className="mt-3 text-xs font-bold text-white/70">
                {text('Loading captured screenshot…', 'جارٍ تحميل لقطة الشاشة الملتقطة…')}
              </p>
            </div>
          </div>
        ) : heroFailed ? (
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <div className="text-3xl text-aered-300">!</div>
              <p className="mt-2 text-xs font-bold">
                {text('Screenshot evidence could not be loaded.', 'تعذر تحميل دليل لقطة الشاشة.')}
              </p>
              <button
                type="button"
                onClick={() => setArtifactRetry((value) => value + 1)}
                className="mt-3 rounded-lg border border-white/20 px-3 py-2 text-[10px] font-bold hover:bg-white/10"
              >
                ↻ {text('Retry evidence', 'إعادة محاولة الدليل')}
              </button>
            </div>
          </div>
        ) : heroUrl ? (
          <>
            <img src={heroUrl} alt={text('Latest authenticated browser screenshot', 'أحدث لقطة متصفح موثقة')} className="h-full w-full object-contain" />
            <div className="pointer-events-none absolute start-3 top-16 rounded-lg border border-aegreen-300/30 bg-aegreen-950/75 px-2 py-1 text-[9px] font-bold text-aegreen-100 backdrop-blur">
              ✓ {text('Captured evidence', 'دليل ملتقط')}
            </div>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-[radial-gradient(circle_at_50%_35%,#173746_0%,#07171f_60%)] text-center">
            <div><div className="text-5xl text-white/20">▧</div><p className="mt-3 text-xs font-bold text-white/50">{loadingDetail ? text('Connecting to browser evidence…', 'جارٍ الاتصال بدليل المتصفح…') : text('No live view or screenshot is available.', 'لا يتوفر عرض مباشر أو لقطة شاشة.')}</p></div>
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-3 top-3 flex flex-wrap items-start justify-between gap-2">
          <div className="rounded-xl border border-white/10 bg-black/65 px-3 py-2 backdrop-blur">
            <div className="text-[9px] font-bold uppercase tracking-wider text-white/50">{text('Phase', 'المرحلة')}</div>
            <div className="mt-0.5 text-xs font-extrabold">{titleCase(selectedJob?.phase ?? 'waiting')}</div>
          </div>
          <div className="flex gap-2">
            <div className="rounded-xl border border-white/10 bg-black/65 px-3 py-2 text-center backdrop-blur"><div className="font-mono text-sm font-extrabold">{selectedJob ? elapsedLabel(selectedJob, now) : '00:00'}</div><div className="text-[8px] uppercase text-white/50">{text('Elapsed', 'المدة')}</div></div>
            <div className="rounded-xl border border-white/10 bg-black/65 px-3 py-2 text-center backdrop-blur"><div className="font-mono text-sm font-extrabold">{coverage}%</div><div className="text-[8px] uppercase text-white/50">{text('Coverage', 'التغطية')}</div></div>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-3 bottom-3 flex flex-wrap items-end justify-between gap-2">
          <div className="max-w-md rounded-xl border border-white/10 bg-black/65 px-3 py-2 backdrop-blur">
            <div className="text-[8px] font-bold uppercase text-white/50">{text('Active check', 'الفحص النشط')}</div>
            <div className="mt-0.5 truncate text-[10px] font-bold">{currentChecklist?.message ?? latestEvent?.message ?? text('Waiting for browser telemetry', 'بانتظار بيانات المتصفح')}</div>
          </div>
          <div className="flex gap-1.5">
            <span className="rounded-lg bg-aered-600/90 px-2 py-1 text-[9px] font-bold">{issueCounts.critical} {text('critical', 'حرج')}</span>
            <span className="rounded-lg bg-camel-600/90 px-2 py-1 text-[9px] font-bold">{issueCounts.high} {text('high', 'عالٍ')}</span>
            <span className="rounded-lg bg-aegold-600/90 px-2 py-1 text-[9px] font-bold">{issueCounts.warning} {text('warnings', 'تحذيرات')}</span>
          </div>
        </div>
      </div>
      <div className="flex min-h-12 flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-[#0c2029] px-3 py-2">
        <div className="flex items-center gap-2 text-[9px] text-white/50">
          <span className={`h-2 w-2 rounded-full ${interactive ? 'animate-pulse bg-aegold-400' : active ? 'animate-pulse bg-aegreen-400' : 'bg-white/30'}`} />
          {interactive
            ? text('Interactive control · activity audited', 'تحكم تفاعلي · النشاط مسجل')
            : text('View-only evidence stream', 'بث أدلة للعرض فقط')}
        </div>
        <div className="flex flex-wrap gap-2">
          {active && canTakeControl && !interactive && (
            <button type="button" disabled={controlling} onClick={() => void takeControl()} className="rounded-lg bg-aegold-500 px-3 py-2 text-[10px] font-extrabold text-[#13222a] disabled:opacity-50">✥ {controlling ? text('Pausing safely…', 'إيقاف آمن…') : text('Take Control', 'تولي التحكم')}</button>
          )}
          {interactive && (
            <button type="button" disabled={controlling} onClick={() => void returnControl()} className="rounded-lg bg-aegreen-500 px-3 py-2 text-[10px] font-extrabold text-[#07171f] disabled:opacity-50">↩ {text('Return Control', 'إعادة التحكم')}</button>
          )}
        </div>
      </div>
    </div>
  );

  if (!config || targets.length === 0) {
    return (
      <GlassCard className="p-8 text-center">
        <div className="text-4xl">◫</div>
        <h2 className="mt-3 text-lg font-extrabold">{text('No published browser targets', 'لا توجد أهداف متصفح منشورة')}</h2>
        <p className="mt-1 text-xs text-ink-faint">{text('Publish a governed Mystery Shopper target before launching a visual run.', 'انشر هدف متسوق خفي محكوماً قبل بدء تشغيل مرئي.')}</p>
      </GlassCard>
    );
  }

  return (
    <section aria-labelledby="mystery-operations-title" className="space-y-4">
      <GlassCard className="overflow-hidden p-0" strong>
        <div className="bg-[radial-gradient(circle_at_85%_15%,rgba(1,115,171,.7),transparent_32%),linear-gradient(120deg,#061720,#0b3548_58%,#084d69)] p-5 text-white">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-aegold-300">{text('Visual service assurance', 'ضمان الخدمات المرئي')}</div>
              <h2 id="mystery-operations-title" className="mt-1 text-2xl font-black">{text('Mystery Shopper Browser Lab', 'مختبر المتسوق الخفي للمتصفح')}</h2>
              <p className="mt-1 text-xs leading-5 text-white/65">{text('Launch governed journeys, watch the browser work, and move from visual evidence to replay-backed fixes.', 'شغّل رحلات محكومة وشاهد المتصفح يعمل وانتقل من الدليل المرئي إلى تحسينات مدعومة بالإعادة.')}</p>
            </div>
            <div className="flex gap-2">
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 backdrop-blur"><div className="text-[9px] uppercase text-white/45">{text('Concurrency', 'التزامن')}</div><div className="font-mono text-lg font-extrabold">{config.maxConcurrentSessions}</div></div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 backdrop-blur"><div className="text-[9px] uppercase text-white/45">{text('Retention', 'الاحتفاظ')}</div><div className="font-mono text-lg font-extrabold">{config.retentionDays}d</div></div>
            </div>
          </div>
          <div className="mt-5 grid gap-3 xl:grid-cols-[minmax(16rem,.8fr)_minmax(0,1.4fr)_auto]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-white/60">
              {text('Published target', 'الهدف المنشور')}
              <select value={targetId} onChange={(event) => setTargetId(event.target.value)} aria-label={text('Published target', 'الهدف المنشور')} className="mt-1 min-h-11 w-full rounded-xl border border-white/15 bg-[#0d2c39] px-3 text-sm font-bold text-white">
                {targets.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name} · {candidate.environment}</option>)}
              </select>
            </label>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/60">{text('Scenarios', 'السيناريوهات')}</div>
              <div className="mt-1 flex min-h-11 flex-wrap gap-2">
                {target?.scenarios.map((scenario) => {
                  const selected = scenarioIds.includes(scenario.id);
                  return <button key={scenario.id} type="button" aria-pressed={selected} onClick={() => setScenarioIds((current) => selected ? current.filter((id) => id !== scenario.id) : [...current, scenario.id])} className={`rounded-full border px-3 py-2 text-[10px] font-bold transition ${selected ? 'border-aegold-300 bg-aegold-400 text-[#14242c]' : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'}`}>{selected ? '✓ ' : ''}{scenario.name[lang]}</button>;
                })}
                {target?.scenarios.length === 0 && <span className="py-2 text-xs text-white/45">{text('Default checklist journey', 'رحلة قائمة الفحص الافتراضية')}</span>}
              </div>
            </div>
            <GlassButton variant="primary" disabled={!target || launching} onClick={() => void launch()} className="self-end min-h-11 px-6">
              {launching ? text('Launching…', 'جارٍ التشغيل…') : `▶ ${text('Run visual shop', 'تشغيل التسوق المرئي')}`}
            </GlassButton>
          </div>
          {target && (
            <div className="mt-4 grid gap-2 text-[10px] text-white/65 sm:grid-cols-2 xl:grid-cols-5">
              <div className="rounded-lg bg-black/15 p-2"><b className="text-aegreen-300">✓</b> {target.sandboxConfirmed ? text('Sandbox actions confirmed', 'إجراءات بيئة الاختبار مؤكدة') : text('Read-only action boundary', 'حدود إجراءات للقراءة فقط')}</div>
              <div className="rounded-lg bg-black/15 p-2">▦ {target.crawl.maxPages} {text('page budget', 'حد الصفحات')}</div>
              <div className="rounded-lg bg-black/15 p-2">◷ {Math.round(target.crawl.timeoutMs / 1000)}s {text('timeout', 'مهلة')}</div>
              <div className="rounded-lg bg-black/15 p-2">◎ {target.allowedDomains.length} {text('allowlisted domains', 'نطاقات مسموحة')}</div>
              <div className="rounded-lg bg-black/15 p-2">▣ {target.checklist.length} {text('evidence checks', 'فحوص الدليل')}</div>
            </div>
          )}
        </div>
      </GlassCard>

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{error}</div></div>}
      {emailNotice && emailNotice.runId === selectedJob?.id && (
        <div role="status" className="aegov-alert alert-success">
          <div className="alert-content text-sm">{emailNotice.message}</div>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[16rem_minmax(0,1fr)]">
        <GlassCard className="min-h-0 p-3">
          <div className="flex items-center justify-between gap-2 px-1 pb-3">
            <div><h3 className="text-sm font-extrabold">{text('Run history', 'سجل التشغيل')}</h3><p className="text-[9px] text-ink-faint">{text('Newest visual sessions', 'أحدث الجلسات المرئية')}</p></div>
            <button type="button" onClick={() => void loadRuns(selectedId)} className="grid h-9 w-9 place-items-center rounded-lg border border-aeblack-100 text-sm hover:bg-aeblack-50" aria-label={text('Refresh runs', 'تحديث التشغيلات')}>↻</button>
          </div>
          <div className="max-h-[40rem] space-y-2 overflow-y-auto pe-1">
            {loadingRuns && [0, 1, 2].map((item) => <div key={item} className="h-20 animate-pulse rounded-xl bg-aeblack-50" />)}
            {!loadingRuns && runs.map((job) => (
              <button type="button" key={job.id} aria-pressed={job.id === selectedId} onClick={() => selectRun(job.id)} className={`w-full rounded-xl border p-3 text-start transition ${job.id === selectedId ? 'border-seablue-400 bg-seablue-50 ring-2 ring-seablue-100' : 'border-aeblack-100 bg-white hover:border-seablue-200'}`}>
                <div className="flex items-center justify-between gap-2"><span className="truncate text-[10px] font-extrabold">{job.targetName}</span><span className={`h-2 w-2 shrink-0 rounded-full ${ACTIVE_STATUSES.has(job.status) ? 'animate-pulse bg-seablue-500' : job.status === 'succeeded' ? 'bg-aegreen-500' : 'bg-aered-500'}`} /></div>
                <div className="mt-1 flex items-end justify-between gap-2"><div><div className="font-mono text-[9px] text-ink-faint">{job.id.slice(0, 8)}</div><div className="mt-1 text-[9px] font-bold text-ink-soft">{titleCase(job.phase)}</div></div><div className="text-end"><div className="font-mono text-sm font-extrabold">{job.score == null ? '—' : Math.round(job.score)}</div><div className="text-[8px] uppercase text-ink-faint">{titleCase(job.status)}</div></div></div>
              </button>
            ))}
            {!loadingRuns && runs.length === 0 && <div className="rounded-xl border border-dashed border-aeblack-200 p-6 text-center text-xs text-ink-faint">{text('No browser runs yet.', 'لا توجد تشغيلات متصفح بعد.')}</div>}
          </div>
        </GlassCard>

        <div className="min-w-0 space-y-4">
          {theater}
          {selectedJob && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-aeblack-100 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill state={runTone(selectedJob.status)} label={titleCase(selectedJob.status)} />
                <span className="font-mono text-[10px] text-ink-faint">{selectedJob.pagesVisited}/{selectedJob.pagesDiscovered} {text('pages', 'صفحات')} · {selectedJob.checklistPassed}/{selectedJob.checklistTotal} {text('checks passed', 'فحوص ناجحة')}</span>
                {selectedJob.error && <span className="text-[10px] font-bold text-reject">{selectedJob.error}</span>}
              </div>
              <div className="flex gap-2">
                {selectedJob.status === 'succeeded' && (
                  <button
                    type="button"
                    disabled={emailingRunId !== null}
                    onClick={() => void emailReport()}
                    className="aegov-btn btn-outline btn-xs min-h-9"
                  >
                    ✉ {emailingRunId === selectedJob.id
                      ? text('Emailing…', 'جارٍ الإرسال…')
                      : text('Email report to me', 'إرسال التقرير إلى بريدي')}
                  </button>
                )}
                {selectedJob.status === 'running' && <button type="button" disabled={controlAction !== null || interactive} onClick={() => void control('pause')} className="aegov-btn btn-outline btn-xs min-h-9">Ⅱ {text('Pause', 'إيقاف مؤقت')}</button>}
                {selectedJob.status === 'paused' && !interactive && <button type="button" disabled={controlAction !== null} onClick={() => void control('resume')} className="aegov-btn btn-outline btn-xs min-h-9">▶ {text('Resume', 'استئناف')}</button>}
                {ACTIVE_STATUSES.has(selectedJob.status) && <button type="button" disabled={controlAction !== null} onClick={() => void control('cancel')} className="aegov-btn btn-outline btn-xs min-h-9 text-reject">× {text('Cancel', 'إلغاء')}</button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {detail && (
        <>
          {replay && replay.pages.length > 1 && (
            <GlassCard className="p-3">
              <div className="flex items-center gap-2 overflow-x-auto">
                <span className="shrink-0 text-[10px] font-bold uppercase text-ink-faint">{text('Replay pages', 'صفحات الإعادة')}</span>
                {replay.pages.map((page, index) => <button key={page.pageId} type="button" aria-pressed={replayPageId === page.pageId} onClick={() => {
                  setReplayPageId(page.pageId);
                  const detailPage = detail.pages.find((candidate) => candidate.pageId === page.pageId);
                  if (detailPage) selectEvidencePage(detailPage.id);
                  setViewMode('replay');
                }} className={`shrink-0 rounded-lg px-3 py-2 text-[10px] font-bold ${replayPageId === page.pageId ? 'bg-seablue-600 text-white' : 'bg-aeblack-50 text-ink-soft'}`}>{text('Page', 'صفحة')} {index + 1} · {Math.round((page.endTimeMs - page.startTimeMs) / 1000)}s</button>)}
              </div>
            </GlassCard>
          )}

          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.35fr)_minmax(20rem,.65fr)]">
            <GlassCard className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2"><div><h3 className="text-sm font-extrabold">{text('Journey coverage map', 'خريطة تغطية الرحلة')}</h3><p className="text-[10px] text-ink-faint">{text('Select a route node to synchronize evidence.', 'اختر عقدة مسار لمزامنة الدليل.')}</p></div><span className="font-mono text-xs font-bold text-oasis">{coverage}%</span></div>
              <RouteCoverageMap detail={detail} selectedPageId={selectedPageId} onSelectPage={(pageId) => {
                selectEvidencePage(pageId);
              }} />
            </GlassCard>

            <GlassCard className="p-4">
              <div className="flex items-center justify-between gap-2"><div><h3 className="text-sm font-extrabold">{text('Synchronized event rail', 'مسار الأحداث المتزامن')}</h3><p className="text-[10px] text-ink-faint">{detail.events.length} {text('redacted events', 'أحداث منقحة')}</p></div><span className={`h-2 w-2 rounded-full ${active ? 'animate-pulse bg-aegreen-500' : 'bg-aeblack-200'}`} /></div>
              <div className="mt-3 max-h-[24rem] space-y-2 overflow-y-auto pe-1">
                {detail.events.map((event) => {
                  const style = EVENT_STYLE[event.kind] ?? EVENT_STYLE.observation!;
                  return (
                    <button type="button" key={event.id} aria-pressed={selectedEventId === event.id} onClick={() => selectEvent(event)} className={`grid w-full grid-cols-[1.75rem_minmax(0,1fr)_auto] items-start gap-2 rounded-xl border p-2 text-start transition ${style.classes} ${selectedEventId === event.id ? 'ring-2 ring-seablue-300' : ''}`}>
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-white/70 text-xs font-black">{style.icon}</span>
                      <span className="min-w-0"><span className="block text-[9px] font-bold uppercase opacity-60">{titleCase(event.kind)} · {titleCase(event.phase)}</span><span className="mt-0.5 block truncate text-[10px] font-bold">{event.message}</span></span>
                      <time className="font-mono text-[8px] opacity-60">{new Date(event.occurredAt).toLocaleTimeString(lang === 'ar' ? 'ar-AE' : 'en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
                    </button>
                  );
                })}
                {detail.events.length === 0 && <p className="py-10 text-center text-xs text-ink-faint">{text('Waiting for browser events…', 'بانتظار أحداث المتصفح…')}</p>}
              </div>
            </GlassCard>
          </div>

          <GlassCard className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div><h3 className="text-sm font-extrabold">{text('Evidence filmstrip', 'شريط لقطات الدليل')}</h3><p className="text-[10px] text-ink-faint">{text('Authenticated artifacts · no public evidence URLs', 'عناصر موثقة · لا روابط دليل عامة')}</p></div>
              <span className="font-mono text-[10px] text-ink-faint">{artifactUrls.size} {text('frames', 'لقطات')}</span>
            </div>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
              {detail.pages.filter((page) => page.screenshotArtifactId && artifactUrls.has(page.screenshotArtifactId)).map((page, index) => (
                <button key={page.id} type="button" onClick={() => { selectEvidencePage(page.id); setViewMode('evidence'); }} className={`group w-52 shrink-0 overflow-hidden rounded-xl border bg-[#07171f] text-start ${selectedArtifactId === page.screenshotArtifactId ? 'border-seablue-400 ring-2 ring-seablue-100' : 'border-aeblack-100'}`}>
                  <div className="aspect-video overflow-hidden"><img src={artifactUrls.get(page.screenshotArtifactId!)} alt={`${text('Browser frame', 'إطار المتصفح')} ${index + 1}`} className="h-full w-full object-cover transition group-hover:scale-105" /></div>
                  <div className="p-2 text-white"><div className="truncate text-[10px] font-bold">{page.title || pagePath(page.url)}</div><div className="mt-0.5 text-[8px] text-white/45">{page.viewport.width ?? '—'}×{page.viewport.height ?? '—'} · {titleCase(page.status)}</div></div>
                </button>
              ))}
              {artifactUrls.size === 0 && <div className="grid h-28 w-full place-items-center rounded-xl border border-dashed border-aeblack-200 text-xs text-ink-faint">{text('Screenshots appear as pages are captured.', 'تظهر اللقطات عند التقاط الصفحات.')}</div>}
            </div>
          </GlassCard>

          <GlassCard className="p-5" strong>
            <div className="grid items-center gap-6 xl:grid-cols-[auto_minmax(0,1fr)]">
              <ProgressRing score={detail.job.score} />
              <div>
                <div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="text-lg font-extrabold">{text('Experience scorecard', 'بطاقة تقييم التجربة')}</h3><p className="mt-1 text-xs text-ink-faint">{detail.job.output?.summary ?? text('Score builds as evidence-backed checks complete.', 'تتكون النتيجة مع اكتمال الفحوص المدعومة بالدليل.')}</p></div>{detail.job.output?.baseline && <span className={`rounded-xl px-3 py-2 font-mono text-xs font-extrabold ${(detail.job.output.baseline.comparison.scoreDelta ?? 0) >= 0 ? 'bg-aegreen-50 text-aegreen-700' : 'bg-aered-50 text-reject'}`}>{(detail.job.output.baseline.comparison.scoreDelta ?? 0) >= 0 ? '▲ +' : '▼ '}{detail.job.output.baseline.comparison.scoreDelta ?? '—'} {text('vs baseline', 'مقابل الأساس')}</span>}</div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {(detail.job.output?.score.dimensions ?? []).map((dimension) => (
                    <div key={dimension.dimension} className="rounded-xl border border-aeblack-100 p-3">
                      <div className="flex items-center justify-between text-[10px]"><span className="font-bold">{titleCase(dimension.dimension)}</span><span className="font-mono font-extrabold">{dimension.score == null ? '—' : `${Math.round(dimension.score)}%`}</span></div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-aeblack-100"><div className={`h-full rounded-full ${(dimension.score ?? 0) >= 80 ? 'bg-aegreen-500' : (dimension.score ?? 0) >= 60 ? 'bg-aegold-500' : 'bg-aered-500'}`} style={{ width: `${dimension.score ?? 0}%` }} /></div>
                      <div className="mt-1 text-[8px] text-ink-faint">{dimension.assessedItems} {text('assessed', 'مقيّم')} · {dimension.unassessedItems} {text('pending', 'معلق')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {detail.job.output?.baseline && (
              <div className="mt-5 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {detail.job.output.baseline.comparison.dimensions.slice(0, 8).map((dimension) => (
                  <div key={dimension.dimension} className="rounded-xl bg-aeblack-50 p-3"><div className="text-[9px] font-bold uppercase text-ink-faint">{titleCase(dimension.dimension)}</div><div className="mt-1 flex items-end justify-between"><div className="font-mono text-lg font-extrabold">{dimension.currentScore ?? '—'}</div><div className={`font-mono text-xs font-bold ${(dimension.delta ?? 0) >= 0 ? 'text-aegreen-700' : 'text-reject'}`}>{dimension.delta == null ? '—' : `${dimension.delta >= 0 ? '+' : ''}${dimension.delta}`}</div></div><div className="text-[8px] text-ink-faint">{text('Baseline', 'الأساس')} {dimension.baselineScore ?? '—'}</div></div>
                ))}
              </div>
            )}
          </GlassCard>

          <div className="grid gap-4 xl:grid-cols-3">
            {([
              ['good', text('What worked', 'ما نجح'), detail.findings.filter((finding) => finding.isPositive), 'border-aegreen-200'],
              ['bad', text('What broke', 'ما تعطل'), detail.findings.filter((finding) => !finding.isPositive && !finding.title.toLowerCase().startsWith('fix next')), 'border-aered-200'],
              ['fix', text('Fix next', 'الإصلاح التالي'), detail.findings.filter((finding) => finding.title.toLowerCase().startsWith('fix next')), 'border-aegold-300'],
            ] as const).map(([key, label, findings, border]) => (
              <GlassCard key={key} className={`border-t-4 p-4 ${border}`}>
                <div className="flex items-center justify-between"><h3 className="text-sm font-extrabold">{label}</h3><span className="rounded-full bg-aeblack-50 px-2 py-1 font-mono text-[10px] font-bold">{findings.length}</span></div>
                <div className="mt-3 max-h-[34rem] space-y-2 overflow-y-auto pe-1">
                  {findings.map((finding) => <FindingCard key={finding.id} finding={finding} selected={selectedFindingId === finding.id} onSelect={() => selectFinding(finding)} onScreenshot={finding.screenshotArtifactId && artifactUrls.has(finding.screenshotArtifactId) ? () => showFindingScreenshot(finding) : null} onReplay={finding.replayTimestampMs !== null && replay ? () => showFindingReplay(finding) : null} />)}
                  {findings.length === 0 && <p className="rounded-xl border border-dashed border-aeblack-200 p-6 text-center text-xs text-ink-faint">{text('No evidence in this column.', 'لا يوجد دليل في هذا العمود.')}</p>}
                </div>
              </GlassCard>
            ))}
          </div>

          <GlassCard className="p-4">
            <div><h3 className="text-sm font-extrabold">{text('Responsive viewport gallery', 'معرض أحجام العرض المتجاوبة')}</h3><p className="text-[10px] text-ink-faint">{text('Published viewport coverage with the selected authenticated frame.', 'تغطية أحجام العرض المنشورة مع الإطار الموثق المحدد.')}</p></div>
            <div className="mt-4 grid items-end gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {(target?.viewports ?? detail.job.target.viewports).map((viewport) => {
                const ratio = viewport.width / viewport.height;
                const width = Math.max(90, Math.min(240, ratio * 150));
                return (
                  <div key={`${viewport.name}-${viewport.width}`} className="text-center">
                    <div className="mx-auto overflow-hidden rounded-xl border-4 border-[#173746] bg-[#07171f] shadow-lg" style={{ width, aspectRatio: `${viewport.width}/${viewport.height}` }}>
                      {heroUrl ? <img src={heroUrl} alt={`${viewport.name} ${text('viewport preview', 'معاينة العرض')}`} className="h-full w-full object-cover object-top" /> : <div className="grid h-full place-items-center text-white/20">▧</div>}
                    </div>
                    <div className="mt-2 text-[10px] font-extrabold">{viewport.name}</div><div className="font-mono text-[8px] text-ink-faint">{viewport.width}×{viewport.height}</div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </>
      )}
    </section>
  );
}
